import { Box, SelectedIcon, Typography, Progress, Button, ForwardIcon } from "@carrotfertility/carotene-core"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { getStepMenuItemDisplayText } from "#/components/home/carrot-plan/CarrotPlansHero"
import React from "react"
import { useIntl, FormattedMessage } from "react-intl"
import { CarrotPlan } from "#/types/carrotPlanTypes"
import useCarrotPlanMenuItems, { CarrotPlanStepMenuItem } from "../hooks/useCarrotPlanMenuItems"
import { CaseRateRenewalModalButtonGroup } from "./CaseRateRenewalModalButtonGroup"
import { CaseRateRenewalModalTopSection } from "./CaseRateRenewalModalTopSection"
import { OPTIONS } from "./steps/CarrotPlanOptionsStep"
import { useCaseRateRenewals } from "./useCaseRateRenewals"
import { useRepublishCarrotPlan } from "./useQueryCaseRateRenewals"
import { useCalibratedEngagementEnabled } from "#/hooks/calibratedEngagement/useCalibratedEngagement"
import { useStartRouting } from "#/components/carrot-plans/shared/useQueryStartRouting"
import { RoutingFlowType } from "#/components/carrot-plans/types/routingFlowType"
import {
  CarrotPlanRequestType,
  ROUTING_STATUS_QUERY_KEY,
  useGetInProgressCarrotPlanRequestType
} from "#/components/carrot-plans/hooks/useGetInProgressCarrotPlanRequestType"
import { useQueryClient } from "@tanstack/react-query"
import { useIsMemberInSensitiveState } from "#/components/carrot-plans/hooks/useIsMemberInSensitiveState"

function DisplayCarrotPlan({ steps }: { steps: CarrotPlanStepMenuItem[] }): JSX.Element {
  return (
    <Box
      padding={(theme) => theme.spacing(theme.tokens.spacing.md)}
      borderRadius={(theme) => theme.tokens.borderRadius.md}
      bgcolor={(theme) => theme.palette.background.default}
    >
      {steps?.map((step) => (
        <Box key={step.menuItemId} display="flex" padding={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
          <Box marginInlineEnd={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
            {step.completedAtUtc !== null ? (
              <SelectedIcon />
            ) : (
              <Box padding={(theme) => theme.spacing(theme.tokens.spacing.sm)} />
            )}
          </Box>
          <Typography>{getStepMenuItemDisplayText(step)}</Typography>
        </Box>
      ))}
    </Box>
  )
}

export function CarrotPlanOptionsContent({ carrotPlan }: { carrotPlan: CarrotPlan }): JSX.Element {
  const intl = useIntl()
  const { locale } = intl
  const { data: carrotPlanStepMenuItems, isLoading: menuItemsLoading } = useCarrotPlanMenuItems(
    carrotPlan?.steps,
    locale
  )
  const { send } = useStateMachine()
  const { mutate, isLoading } = useRepublishCarrotPlan()
  const { setRequestError, setDisableModalCloseOnLoading } = useCaseRateRenewals()
  const calibratedEngagementEnabledQuery = useCalibratedEngagementEnabled()
  const { startRouting } = useStartRouting(RoutingFlowType.CASE_RATE_RENEWAL)
  const inProgressCarrotPlanRequestType = useGetInProgressCarrotPlanRequestType()
  const queryClient = useQueryClient()
  const isMemberInSensitiveState = useIsMemberInSensitiveState()

  const isCalibratedEngagement = calibratedEngagementEnabledQuery.data
  const shouldUseSimplifiedUI = isCalibratedEngagement || isMemberInSensitiveState

  const getHeader = () => {
    if (isMemberInSensitiveState) {
      return intl.formatMessage({
        defaultMessage: "Checking in"
      })
    }
    if (isCalibratedEngagement) {
      return intl.formatMessage({
        defaultMessage: "Let's update your Carrot Plan."
      })
    }
    return intl.formatMessage({
      defaultMessage: "Here's your most recent Carrot Plan. Would you like some new suggestions?"
    })
  }

  const getBody = () => {
    if (shouldUseSimplifiedUI && inProgressCarrotPlanRequestType.data === CarrotPlanRequestType.RENEWAL_INTAKE) {
      return intl.formatMessage({
        defaultMessage: "Continue telling us about your journey to receive new suggestions for next steps."
      })
    } else if (isMemberInSensitiveState) {
      return intl.formatMessage({
        defaultMessage:
          "It’s been a while since you told us your journey was not going as planned, and we’d like to continue offering support for the road ahead. If you're ready, please answer a few questions so we can make some new suggestions for next steps."
      })
    }
    return intl.formatMessage({
      defaultMessage:
        "It’s been a while since we last updated your Carrot Plan with new suggestions. Please answer a few questions to receive new suggestions for next steps. "
    })
  }

  const getPrimaryButtonCopy = () => {
    if (inProgressCarrotPlanRequestType.data === CarrotPlanRequestType.RENEWAL_INTAKE) {
      return intl.formatMessage({
        defaultMessage: "Continue"
      })
    }
    return intl.formatMessage({
      defaultMessage: "Share an update"
    })
  }

  function onError(): void {
    setRequestError(true)
    setDisableModalCloseOnLoading(false)
    send("ERROR")
  }

  function onSuccess(): void {
    send(OPTIONS.KEEP_MY_PLAN)
    setDisableModalCloseOnLoading(false)
    queryClient.refetchQueries([ROUTING_STATUS_QUERY_KEY])
  }

  async function keepMyPlan(): Promise<void> {
    setDisableModalCloseOnLoading(true)
    mutate(null, {
      onSuccess,
      onError
    })
  }

  return menuItemsLoading || calibratedEngagementEnabledQuery.isLoading ? (
    <Progress />
  ) : (
    <>
      <CaseRateRenewalModalTopSection header={getHeader()} />
      <Box>
        <Typography variant="body1" color={(theme) => theme.palette.text.secondary}>
          {getBody()}
        </Typography>
        {!shouldUseSimplifiedUI && (
          <Box paddingY={(theme) => theme.spacing(theme.tokens.spacing.md)}>
            <DisplayCarrotPlan steps={carrotPlanStepMenuItems} />
          </Box>
        )}
        {!shouldUseSimplifiedUI && (
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="text"
              color="secondary"
              disabled={isLoading}
              onClick={() => send(OPTIONS.DONE_FOR_NOW)}
              endIcon={<ForwardIcon />}
            >
              <FormattedMessage defaultMessage="I'm done using Carrot" />
            </Button>
          </Box>
        )}
        <CaseRateRenewalModalButtonGroup
          buttonOne={
            shouldUseSimplifiedUI ? (
              <Button
                variant="outlined"
                color="inherit"
                disabled={isLoading}
                onClick={() => send(OPTIONS.DONE_FOR_NOW)}
              >
                <FormattedMessage defaultMessage="I'm done using Carrot" />
              </Button>
            ) : (
              <Button variant="outlined" color="inherit" disabled={isLoading} onClick={keepMyPlan}>
                <FormattedMessage defaultMessage="Keep my plan" />
              </Button>
            )
          }
          buttonTwo={
            <Button disabled={isLoading} variant="contained" onClick={() => startRouting()}>
              {getPrimaryButtonCopy()}
            </Button>
          }
        />
      </Box>
    </>
  )
}
